import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { setPolicyTypes } from '../components/communications/segmentRuleHelpers';
import { bookable, published } from '../helpers/policyLines.js';

/**
 * The hub's policy lines, fetched once for whoever asks.
 *
 * Four surfaces need this list — the Policy lines page, the registration form
 * builder, the policy picker and tags on a record, and the segment builder —
 * and before XN-4535 the last three each carried a hard-coded copy instead.
 * One store is what makes a rename on the Policy lines page show up on the
 * other three without a reload, which is the story's whole point.
 *
 * Not persisted: it is hub configuration that another owner may have changed
 * since, and it is one cheap request.
 */
export const usePolicyLinesStore = defineStore('policyLines', () => {
  const lines = ref([]);
  const icons = ref([]);
  const colors = ref([]);
  const isLoaded = ref(false);
  const isLoading = ref(false);

  /** Named and unarchived — what every surface but this page may offer. */
  const options = computed(() => published(lines.value));

  /** Those a policy can actually be booked against. See helpers/policyLines. */
  const policyTypes = computed(() => bookable(lines.value));

  const byId = computed(() => new Map(lines.value.map((line) => [line.id, line])));

  const api = () => window.store?.event_backend_api ?? '/api';

  /**
   * `withCounts` costs an extra aggregate query, so only the page that draws
   * "860 policyholders" asks for it.
   *
   * A concurrent second call is not deduped on purpose: the only caller that
   * would race is the page reloading after its own save, and that one wants
   * the newer answer.
   */
  const load = async ({ force = false, withCounts = false } = {}) => {
    if (isLoaded.value && !force && !withCounts) return lines.value;

    isLoading.value = true;

    try {
      const { data } = await axios.get(`${api()}/policy-line-tags`, {
        params: withCounts ? { with_counts: 1 } : {},
      });

      lines.value = data?.data?.tags ?? [];
      icons.value = data?.data?.icons ?? [];
      colors.value = data?.data?.colors ?? [];
      isLoaded.value = true;
      syncSegmentLabels();
    } catch (error) {
      console.error('Failed to load policy lines:', error);
      // Left as-is rather than emptied: a page already showing the list should
      // not blank it because one refresh failed.
    } finally {
      isLoading.value = false;
    }

    return lines.value;
  };

  /**
   * The segment builder reads a module-level registry rather than this store,
   * because its formatters are pure functions. Keeping that registry derived
   * from here is what makes a rename show up in segment labels — without this
   * the registry stayed empty in production and `policy_type` rules could not
   * be built at all, which the store's own docblock claimed was handled.
   */
  const syncSegmentLabels = () => setPolicyTypes(lines.value);

  /** Fold one saved line back in, so every surface updates without a refetch. */
  const upsert = (line) => {
    if (!line?.id) return;

    const index = lines.value.findIndex((existing) => existing.id === line.id);

    if (index === -1) lines.value = [...lines.value, line];
    else lines.value = lines.value.map((existing, i) => (i === index ? line : existing));

    syncSegmentLabels();
  };

  const remove = (id) => {
    lines.value = lines.value.filter((line) => line.id !== id);
    syncSegmentLabels();
  };

  return {
    lines,
    icons,
    colors,
    isLoaded,
    isLoading,
    options,
    policyTypes,
    byId,
    load,
    upsert,
    remove,
  };
});
